const MS_PER_DAY = 24 * 60 * 60 * 1000;

export function normalizeCampaignsResponse(payload) {
    if (Array.isArray(payload)) {
        return {
            campaigns: payload,
            dashboard: null,
        };
    }

    if (!payload || typeof payload !== 'object') {
        return {
            campaigns: [],
            dashboard: null,
        };
    }

    const campaigns = firstArray(
        payload.campaigns,
        payload.items,
        payload.results,
        payload.data,
        payload.rows
    );

    return {
        campaigns,
        dashboard: firstObject(payload.dashboard, payload.summary, payload.metrics, payload.analytics),
    };
}

export function getCampaignId(campaign) {
    return firstValue(campaign?.id, campaign?._id, campaign?.campaignId, campaign?.campaign_id);
}

export function getCampaignName(campaign) {
    return firstValue(campaign?.name, campaign?.title, campaign?.campaignName, campaign?.campaign_name, 'Sem nome');
}

export function getCampaignDescription(campaign) {
    return firstValue(campaign?.description, campaign?.summary, campaign?.campaignDescription, campaign?.campaign_description, '');
}

export function getCampaignCreatedAt(campaign) {
    return firstValue(campaign?.createdAt, campaign?.created_at, campaign?.created, campaign?.insertedAt, campaign?.inserted_at);
}

export function getCampaignTotalLeads(campaign) {
    return toNumber(firstValue(
        campaign?.totalLeads,
        campaign?.total_leads,
        campaign?.leadCount,
        campaign?.lead_count,
        campaign?.leadsGenerated,
        campaign?.leads_generated,
        Array.isArray(campaign?.leads) ? campaign.leads.length : undefined
    ));
}

export function getCampaignInProgress(campaign) {
    return toNumber(firstValue(
        campaign?.leadsInProgress,
        campaign?.leads_in_progress,
        campaign?.inProgressLeads,
        campaign?.in_progress_leads,
        campaign?.processingLeads,
        campaign?.processing_leads
    ));
}

export function getCampaignCompleted(campaign) {
    return toNumber(firstValue(
        campaign?.completedLeads,
        campaign?.completed_leads,
        campaign?.finishedLeads,
        campaign?.finished_leads,
        campaign?.convertedLeads,
        campaign?.converted_leads,
        campaign?.wonLeads,
        campaign?.won_leads
    ));
}

export function getCampaignCreditsSpent(campaign) {
    return toNumber(firstValue(
        campaign?.creditsSpent,
        campaign?.credits_spent,
        campaign?.creditsUsed,
        campaign?.credits_used,
        campaign?.leadsToConsume,
        campaign?.leads_to_consume,
        getCampaignTotalLeads(campaign)
    ));
}

export function getCampaignParticipants(campaign) {
    return firstArray(campaign?.participants, campaign?.members, campaign?.teamMembers, campaign?.team_members);
}

export function getCampaignStatus(campaign) {
    return String(firstValue(campaign?.status, campaign?.state, campaign?.campaignStatus, campaign?.campaign_status, 'active')).toLowerCase();
}

export function getCampaignQualityLabel(campaign) {
    const label = firstValue(campaign?.qualityLabel, campaign?.quality_label, campaign?.leadQuality, campaign?.lead_quality);
    if (label) return String(label);

    const qualityScore = toNullableNumber(firstValue(campaign?.qualityScore, campaign?.quality_score));
    if (qualityScore !== null && qualityScore >= 80) return 'Alta qualidade';

    if (campaign?.highQuality || campaign?.high_quality) return 'Alta qualidade';

    return '';
}

export function buildCampaignDashboard(campaigns, backendDashboard = null, now = new Date()) {
    const campaignList = Array.isArray(campaigns) ? campaigns : [];
    const dashboard = backendDashboard || {};
    const currentRange = getMonthRange(now, 0);
    const previousRange = getMonthRange(now, -1);

    const currentCampaigns = campaignList.filter((campaign) => isDateInRange(getCampaignCreatedAt(campaign), currentRange));
    const previousCampaigns = campaignList.filter((campaign) => isDateInRange(getCampaignCreatedAt(campaign), previousRange));

    const totalCampaigns = toNumber(firstValue(
        dashboard.totalCampaigns,
        dashboard.total_campaigns,
        campaignList.length
    ));
    const leadsGenerated = toNumber(firstValue(
        dashboard.leadsGenerated,
        dashboard.leads_generated,
        dashboard.totalLeads,
        dashboard.total_leads,
        sumBy(campaignList, getCampaignTotalLeads)
    ));
    const completedLeads = toNumber(firstValue(
        dashboard.completedLeads,
        dashboard.completed_leads,
        sumBy(campaignList, getCampaignCompleted)
    ));
    const creditsSpent = toNumber(firstValue(
        dashboard.creditsSpent,
        dashboard.credits_spent,
        dashboard.creditsUsed,
        dashboard.credits_used,
        sumBy(campaignList, getCampaignCreditsSpent)
    ));
    const conversionRate = toNumber(firstValue(
        dashboard.conversionRate,
        dashboard.conversion_rate,
        ratio(completedLeads, leadsGenerated)
    ));
    const creditLimit = toNullableNumber(firstValue(
        dashboard.creditLimit,
        dashboard.credit_limit,
        dashboard.creditsLimit,
        dashboard.credits_limit,
        dashboard.totalCredits,
        dashboard.total_credits
    ));

    return {
        totalCampaigns,
        leadsGenerated,
        completedLeads,
        conversionRate,
        creditsSpent,
        creditLimit,
        creditsProgress: creditLimit ? Math.min((creditsSpent / creditLimit) * 100, 100) : Math.min(ratio(creditsSpent, leadsGenerated), 100),
        activeCampaigns: campaignList.filter((campaign) => isActiveStatus(getCampaignStatus(campaign))).length,
        draftCampaigns: campaignList.filter((campaign) => isDraftStatus(getCampaignStatus(campaign))).length,
        pausedCampaigns: campaignList.filter((campaign) => isPausedStatus(getCampaignStatus(campaign))).length,
        deltas: {
            totalCampaigns: firstDelta(
                dashboard.totalCampaignsDelta,
                dashboard.total_campaigns_delta,
                percentDelta(currentCampaigns.length, previousCampaigns.length)
            ),
            leadsGenerated: firstDelta(
                dashboard.leadsGeneratedDelta,
                dashboard.leads_generated_delta,
                percentDelta(sumBy(currentCampaigns, getCampaignTotalLeads), sumBy(previousCampaigns, getCampaignTotalLeads))
            ),
            conversionRate: firstDelta(
                dashboard.conversionRateDelta,
                dashboard.conversion_rate_delta,
                percentDelta(
                    ratio(sumBy(currentCampaigns, getCampaignCompleted), sumBy(currentCampaigns, getCampaignTotalLeads)),
                    ratio(sumBy(previousCampaigns, getCampaignCompleted), sumBy(previousCampaigns, getCampaignTotalLeads))
                )
            ),
            creditsSpent: firstDelta(
                dashboard.creditsSpentDelta,
                dashboard.credits_spent_delta,
                percentDelta(sumBy(currentCampaigns, getCampaignCreditsSpent), sumBy(previousCampaigns, getCampaignCreditsSpent))
            ),
        },
    };
}

export function filterCampaigns(campaigns, query, statusFilter) {
    const searchTerm = String(query || '').trim().toLowerCase();

    return (campaigns || []).filter((campaign) => {
        const status = getCampaignStatus(campaign);
        const matchesStatus = statusFilter === 'all' || getStatusGroup(status) === statusFilter;

        if (!matchesStatus) return false;

        if (!searchTerm) return true;

        return [
            getCampaignName(campaign),
            getCampaignDescription(campaign),
            status,
        ].some((value) => String(value || '').toLowerCase().includes(searchTerm));
    });
}

export function sortCampaigns(campaigns, orderBy, order) {
    const direction = order === 'desc' ? -1 : 1;
    const withIndex = (campaigns || []).map((campaign, index) => ({campaign, index}));

    withIndex.sort((left, right) => {
        const leftValue = getSortValue(left.campaign, orderBy);
        const rightValue = getSortValue(right.campaign, orderBy);
        const comparison = compareValues(leftValue, rightValue);
        if (comparison !== 0) return comparison * direction;
        return left.index - right.index;
    });

    return withIndex.map((item) => item.campaign);
}

export function getStatusGroup(status) {
    if (isDraftStatus(status)) return 'draft';
    if (isPausedStatus(status)) return 'paused';
    if (isActiveStatus(status)) return 'active';
    return 'inactive';
}

export function toNumber(value) {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
}

function firstValue(...values) {
    return values.find((value) => value !== undefined && value !== null && value !== '');
}

function firstArray(...values) {
    return values.find((value) => Array.isArray(value)) || [];
}

function firstObject(...values) {
    return values.find((value) => value && typeof value === 'object' && !Array.isArray(value)) || null;
}

function firstDelta(...values) {
    return toNumber(firstValue(...values));
}

function toNullableNumber(value) {
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
}

function sumBy(items, getter) {
    return (items || []).reduce((total, item) => total + toNumber(getter(item)), 0);
}

function ratio(numerator, denominator) {
    const bottom = toNumber(denominator);
    if (!bottom) return 0;
    return (toNumber(numerator) / bottom) * 100;
}

function percentDelta(current, previous) {
    const currentValue = toNumber(current);
    const previousValue = toNumber(previous);

    if (!previousValue) {
        return currentValue ? 100 : 0;
    }

    return ((currentValue - previousValue) / previousValue) * 100;
}

function getMonthRange(now, offset) {
    const date = new Date(now);
    const start = new Date(date.getFullYear(), date.getMonth() + offset, 1);
    const end = new Date(date.getFullYear(), date.getMonth() + offset + 1, 1);

    return {start, end};
}

function isDateInRange(value, range) {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return false;

    return date >= range.start && date < range.end;
}

function isActiveStatus(status) {
    return ['active', 'ativo', 'enabled'].includes(status);
}

function isDraftStatus(status) {
    return ['draft', 'rascunho'].includes(status);
}

function isPausedStatus(status) {
    return ['paused', 'pausado', 'pause'].includes(status);
}

function getSortValue(campaign, orderBy) {
    if (orderBy === 'createdAt') return new Date(getCampaignCreatedAt(campaign)).getTime() || 0;
    if (orderBy === 'totalLeads') return getCampaignTotalLeads(campaign);
    if (orderBy === 'leadsInProgress') return getCampaignInProgress(campaign);
    if (orderBy === 'completedLeads') return getCampaignCompleted(campaign);
    if (orderBy === 'status') return getCampaignStatus(campaign);
    return getCampaignName(campaign).toLowerCase();
}

function compareValues(leftValue, rightValue) {
    if (typeof leftValue === 'number' && typeof rightValue === 'number') {
        return leftValue - rightValue;
    }

    return String(leftValue).localeCompare(String(rightValue), 'pt-BR');
}

export function daysSince(value, now = new Date()) {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return null;

    return Math.max(0, Math.floor((now.getTime() - date.getTime()) / MS_PER_DAY));
}
